<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import i18next from 'i18next'

const props = defineProps({
    context: {
        type: String,
        default: 'client',
        validator: (value) => ['admin', 'client'].includes(value)
    },
    modelValue: {
        type: String,
        default: null
    }
})

const emit = defineEmits(['update:modelValue'])

const getLanguageKey = () => `language.${props.context}`
const getStoredLanguage = () => localStorage.getItem(getLanguageKey())
const setStoredLanguage = (language) => localStorage.setItem(getLanguageKey(), language)

const currentLanguage = ref(i18next.language || 'en')

watch(() => props.modelValue, (newValue) => {
    if (newValue && newValue !== currentLanguage.value) {
        currentLanguage.value = newValue
        i18next.changeLanguage(newValue)
        setStoredLanguage(newValue)
    }
}, { immediate: true })

watch(currentLanguage, (newValue) => {
    emit('update:modelValue', newValue)
}, { immediate: true })

const availableLanguages = [
    { code: 'en', name: 'English (US)', flag: 'flag-us' },
    { code: 'es', name: 'Español (ES)', flag: 'flag-es' },
    { code: 'pt', name: 'Português (BR)', flag: 'flag-br' }
]

const showMenu = ref(false)

const getFlag = () => {
    const lang = availableLanguages.find(lang => lang.code === currentLanguage.value)
    return lang ? lang.flag : 'flag-us'
}

const changeLanguage = async (code) => {
    currentLanguage.value = code
    await i18next.changeLanguage(code)

    if (!props.modelValue) {
        setStoredLanguage(code)
    }
    
    showMenu.value = false

    window.dispatchEvent(
        new CustomEvent('languageChanged', {
            detail: { language: code, context: props.context }
        })
    )
    
    window.dispatchEvent(
        new CustomEvent(`language-changed-${props.context}`, {
            detail: { language: code }
        })
    )
}

const toggleMenu = () => {
    showMenu.value = !showMenu.value
}

const clickOutside = (event) => {
    const dropdown = document.querySelector('.language-selector-wrapper')
    if (dropdown && !dropdown.contains(event.target)) {
        showMenu.value = false
    }
}

const handleLanguageChange = (event) => {
    const newLanguage = event.detail.language
    currentLanguage.value = newLanguage
    i18next.changeLanguage(newLanguage)
}

onMounted(() => {
    let initialLanguage = props.modelValue || getStoredLanguage() || i18next.language || 'en'
    currentLanguage.value = initialLanguage
    i18next.changeLanguage(initialLanguage)
    
    if (!props.modelValue) {
        setStoredLanguage(initialLanguage)
    }

    i18next.on('languageChanged', (lng) => {
        currentLanguage.value = lng
    })

    const eventName = `language-changed-${props.context}`
    window.addEventListener(eventName, handleLanguageChange)
    document.addEventListener('click', clickOutside)
})

onUnmounted(() => {
    window.removeEventListener(`language-changed-${props.context}`, handleLanguageChange)
    document.removeEventListener('click', clickOutside)
})
</script>

<template>
    <div class="language-selector-wrapper ">
        <div class="flag-button" @click="toggleMenu">
            <span :class="['flag', getFlag()]"></span>
        </div>

        <transition name="fade-scale">
            <div v-if="showMenu" class="language-menu">
                <div v-for="lang in availableLanguages" :key="lang.code" class="language-option"
                    @click="changeLanguage(lang.code)">
                    <span :class="['flag', lang.flag]"></span>
                </div>
            </div>
        </transition>
    </div>
</template>

<style scoped>
.language-selector-wrapper {
    position: relative;
    width: 40px;
}

.flag-button {
    width: 40px;
    height: 40px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
}

.flag-button:hover {
    background: #f9fafb;
}

.flag {
    width: 28px;
    height: 20px;
    border-radius: 4px;
    border: 1px solid #fff;
    display: inline-block;
    line-height: 1;
    overflow: hidden;
}

.language-menu {
    position: absolute;
    top: 50px;
    left: 0;
    width: 50px;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 50;
    overflow: hidden;
}

.language-option {
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s;
    border-radius: 6px;
    margin: 2px;
}

.language-option:hover {
    background: #f3f4f6;
}

.fade-scale-enter-active,
.fade-scale-leave-active {
    transition: all 0.15s ease-out;
}

.fade-scale-enter-from {
    opacity: 0;
    transform: scale(0.95) translateY(-4px);
}

.fade-scale-enter-to {
    opacity: 1;
    transform: scale(1) translateY(0);
}

.fade-scale-leave-from {
    opacity: 1;
    transform: scale(1) translateY(0);
}

.fade-scale-leave-to {
    opacity: 0;
    transform: scale(0.95) translateY(-4px);
}
</style>
